import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { LOGIN } from '../../constant';
import { User, UserRole } from '../../models/user.model';
import { LoginRequest } from '../auth/models/login-request.model';
import { LoginResponse } from '../auth/models/login-response.model';

export type { UserRole };

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly server_url = environment.apiUrl;
  private readonly http = inject(HttpClient);

  private readonly tokenKey = 'access_token';
  private readonly userKey = 'auth_user';

  private readonly _user = new BehaviorSubject<User | null>(this.restoreUser());

  readonly user$ = this._user.asObservable();
  readonly currentUser = signal<User | null>(this._user.value);

  private readonly sessionMessageKey = 'session_message';

  constructor() {
    if (this.getToken() && this.isTokenExpired()) {
      this.logout('expired');
    }
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const request: LoginRequest = { email, password };

    return this.http.post<LoginResponse>(this.server_url + LOGIN, request)
      .pipe(tap(response => this.storeAuthentication(response)));
  }

  logout(reason?: 'expired' | 'invalid'): void {
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.userKey);

    if (reason) {
      sessionStorage.setItem(this.sessionMessageKey, reason);
    }

    this._user.next(null);
    this.currentUser.set(null);
  }

  consumeSessionMessage(): 'expired' | 'invalid' | null {
    const reason = sessionStorage.getItem(this.sessionMessageKey) as 'expired' | 'invalid' | null;
    if (reason) {
      sessionStorage.removeItem(this.sessionMessageKey);
    }
    return reason;
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();

    if (!token || !this._user.value) return false;

    if (this.isTokenExpired(token)) {
      this.logout('expired');
      return false;
    }

    return true;
  }

  isTokenExpired(token = this.getToken()): boolean {
    if (!token) return true;

    try {
      const payload = JSON.parse(this.decodeBase64Url(token.split('.')[1]));

      if (!payload.exp) return true;

      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  hasRole(...roles: UserRole[]): boolean {
    const user = this._user.value;
    return this.isAuthenticated() && !!user && roles.includes(user.role);
  }

  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  canEdit(): boolean {
    return this.hasRole('ADMIN', 'MANAGER');
  }

  canSell(): boolean {
    return this.hasRole('ADMIN', 'MANAGER', 'COMMERCIAL');
  }

  get value(): User | null {
    return this._user.value;
  }

  private storeAuthentication(response: LoginResponse): void {
    sessionStorage.setItem(this.tokenKey, response.token);
    sessionStorage.setItem(this.userKey, JSON.stringify(response.user));

    this._user.next(response.user);
    this.currentUser.set(response.user);
  }

  private restoreUser(): User | null {
    try {
      const token = sessionStorage.getItem(this.tokenKey);
      const raw = sessionStorage.getItem(this.userKey);

      if (!token || !raw || this.isTokenExpired(token)) {
        sessionStorage.removeItem(this.tokenKey);
        sessionStorage.removeItem(this.userKey);
        return null;
      }

      return JSON.parse(raw) as User;
    } catch {
      sessionStorage.removeItem(this.tokenKey);
      sessionStorage.removeItem(this.userKey);
      return null;
    }
  }

  private decodeBase64Url(value: string): string {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - base64.length % 4) % 4);

    return decodeURIComponent(
      atob(base64 + padding)
        .split('')
        .map(char => '%' + char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
  }
}